/**
 * Depth-only ← target for fixed CustomerShellHeader.
 * Always returns a target so Zpět stays in the header on every screen:
 * step / link when a parent or visit exists; inert (disabled) only when the
 * user has nowhere left to go (fresh app / empty history) — not because the
 * tab is Organizace.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import {
  browseVisitHref,
  isBrowseVisitStackablePath,
  markBrowseVisitBackNavigation,
  peekSafeBrowseVisit,
  popSafeBrowseVisit,
} from '../navigation/browseVisitStack.js';
import { isEphemeralBackRestorePath } from '../navigation/ephemeralBackRestore.js';
import { resolveHierarchicalBackTarget } from '../navigation/hierarchicalBack.js';
import { isCustomerBrowseEnabled } from '../config/customerFeatureFlags.js';
import {
  resolveBootCustomerFeatureFlags,
  resolvePlatformHubPath,
} from '../features/shop/resolveShopHref.js';
import { useOptionalHierarchicalBackFlow } from '../shared/context/HierarchicalBackFlowContext.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';

export type ShellDepthBackTarget =
  | { readonly mode: 'step'; readonly onBack: () => void; readonly disabled: boolean }
  | { readonly mode: 'link'; readonly to: string }
  /** Nowhere left to go — chrome Zpět visible but non-navigating. */
  | { readonly mode: 'inert'; readonly disabled: true };

function normalizePathname(value: string): string {
  const noHash = value.split('#', 1)[0] ?? value;
  const noQuery = noHash.split('?', 1)[0] ?? noHash;
  const trimmed = noQuery.trim();
  if (trimmed.length <= 1) {
    return trimmed === '' ? '/' : trimmed;
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

function resolvesToCurrentPath(stepBackTo: string, currentPathname: string): boolean {
  const normalizedCurrent = normalizePathname(currentPathname);
  try {
    const parsed = new URL(stepBackTo, 'https://rpapp.local');
    return normalizePathname(parsed.pathname) === normalizedCurrent;
  } catch {
    return normalizePathname(stepBackTo) === normalizedCurrent;
  }
}

function isUnsafeHierarchicalParent(parentPath: string): boolean {
  try {
    const parsed = new URL(parentPath, 'https://rpapp.local');
    return isEphemeralBackRestorePath(parsed.pathname);
  } catch {
    return isEphemeralBackRestorePath(parentPath);
  }
}

/**
 * Prefer topmost overlay dismiss (same order as OS/POP), then published same-URL
 * step handler, then stepBackTo, then browse visit stack (where I was — skipping
 * ephemeral payment surfaces), then named hierarchical parent.
 * Falls back to inert only when nothing safe remains.
 */
export function useShellDepthBack(): ShellDepthBackTarget {
  const location = useLocation();
  const navigate = useNavigate();
  const tenantCode = useTenantCode();
  const flowCtx = useOptionalHierarchicalBackFlow();
  const overlayCtx = useOptionalOverlayBackDismiss();
  const overlayDismiss = overlayCtx?.overlayDismiss ?? null;

  // Match OS/POP: dismiss open sheets (Collect, Cart, …) before journey step/nav.
  // Collect Policy A leaves shell inert; continuePending no-op dismiss (returns false) is intentional — do not force disabled:true.
  if (overlayDismiss !== null) {
    return {
      mode: 'step',
      disabled: false,
      onBack: (): void => {
        overlayDismiss();
      },
    };
  }

  if (flowCtx?.hasOnStepBack === true) {
    return {
      mode: 'step',
      disabled: flowCtx.backDisabled === true,
      onBack: (): void => {
        flowCtx.consumeStepBack();
      },
    };
  }

  const stepBackTo = flowCtx?.stepBackTo ?? null;
  if (stepBackTo !== null && stepBackTo.length > 0) {
    if (
      !resolvesToCurrentPath(stepBackTo, location.pathname) &&
      !isUnsafeHierarchicalParent(stepBackTo)
    ) {
      return { mode: 'link', to: stepBackTo };
    }
  }

  // Browse visit stack — remember prior steps; never restore payment monitoring / QR.
  if (isBrowseVisitStackablePath(location.pathname)) {
    const prior = peekSafeBrowseVisit(isEphemeralBackRestorePath);
    if (prior !== null) {
      return {
        mode: 'step',
        disabled: false,
        onBack: (): void => {
          const entry = popSafeBrowseVisit(isEphemeralBackRestorePath);
          if (entry === null) {
            return;
          }
          markBrowseVisitBackNavigation();
          const href = browseVisitHref(entry);
          const pathOnly = href.split('?')[0] ?? href;
          const flags = resolveBootCustomerFeatureFlags();
          if (
            !isCustomerBrowseEnabled(flags) &&
            (pathOnly === '/browse' || pathOnly.startsWith('/browse/'))
          ) {
            navigate(resolvePlatformHubPath(flags));
          } else {
            navigate(href);
          }
        },
      };
    }
  }

  const hierarchical = resolveHierarchicalBackTarget(location.pathname, tenantCode, {
    search: location.search,
  });
  const parentPath = hierarchical?.parentPath ?? null;
  if (
    parentPath !== null &&
    parentPath.length > 0 &&
    !isUnsafeHierarchicalParent(parentPath)
  ) {
    return { mode: 'link', to: parentPath };
  }

  return { mode: 'inert', disabled: true };
}
